/**
 * Style seed data + pure merge helpers (H-2026-09-12-128) — the Inspiration
 * wall (2a) and the Community style cards (2b) both render from the SAME
 * seed list: style_registry rows (GET /api/customer/styles) joined with a
 * deterministic per-style image/color.
 *
 * Real sample photography lives on the style_registry rows themselves
 * (sample_image_url, D1) and wins wherever the caller has the row; manual R2
 * overrides live in STYLE_SEED_URLS; the generated placehold.co tile keyed
 * off the palette color is the last-resort fallback for rows with no real
 * sample.
 *
 * This module is PURE (no DOM, no fetch) so the helpers are unit-testable;
 * the fetch/render halves live in inspiration-wall.js / community-styles.js.
 */

// style_registry id → palette hex (no '#'). Covers the seeded pool
// (7 resident + 6 explore, H-2026-09-12-127); unknown ids fall back to a
// hash-picked neutral so the wall never breaks on a new registry row.
export const STYLE_SEED_COLORS = {
  'retro-badge': 'b5651d',
  'minimal-line': '4a4e69',
  'bold-typo': '22223b',
  watercolor: '6d9dc5',
  'flat-vector': '2a9d8f',
  'ghibli-soft': '8ab17d',
  'woodcut-bw': '3d3d3d',
  'neo-brutalism': 'e76f51',
  'mamamiya-print': '2d6a4f',
  'riso-pop': 'ef476f',
  'paper-collage': 'a98467',
  'ink-wash': '5c677d',
  'pixel-art': '7d5ba6',
}

// The resident ids inside STYLE_SEED_COLORS — used to synthesize fallback
// resident cards when the API is unreachable (Community tab, card 2b).
export const KNOWN_RESIDENT_IDS = [
  'retro-badge',
  'minimal-line',
  'bold-typo',
  'watercolor',
  'flat-vector',
  'ghibli-soft',
  'woodcut-bw',
]

// Real R2 seed URLs land HERE (Hermes, acceptance-time) — an entry overrides
// the generated placeholder from seedImageUrl for that style id.
export const STYLE_SEED_URLS = {}

// The wall must never render below this many tiles (acceptance #2: ≥8).
export const MIN_WALL_ITEMS = 8

const FALLBACK_COLORS = ['6c757d', '8d99ae', '457b9d', 'a26769', '7f5539']

/** 'ghibli-soft' → 'Ghibli Soft' */
export function humanizeStyleId(id) {
  return String(id)
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

/** Deterministic palette color for a style id (hex, no '#'). */
export function seedColor(id) {
  const key = String(id)
  if (STYLE_SEED_COLORS[key]) return STYLE_SEED_COLORS[key]
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length]
}

/**
 * Display image for a style: the row's real sample_image_url when the caller
 * has one, then the R2 override when configured, otherwise a generated 4:5
 * placeholder tile in the style's palette color.
 * @param {string} id - style_registry id
 * @param {string} [label] - human label baked into the placeholder text
 * @param {string} [sampleUrl] - real sample_image_url from the style_registry row
 * @returns {string} image URL
 */
export function seedImageUrl(id, label, sampleUrl) {
  if (sampleUrl) return sampleUrl
  if (STYLE_SEED_URLS[id]) return STYLE_SEED_URLS[id]
  const text = encodeURIComponent(label || humanizeStyleId(id))
  return `https://placehold.co/512x640/${seedColor(id)}/ffffff/png?text=${text}`
}

/**
 * Join style_registry rows with their seed display images.
 * lora_id/lora_strength pass through so intent surfaces (H-138 middle
 * column) can badge LoRA-backed styles without a second fetch.
 * prompt_fragment/restyle_prompt pass through so a generate surface (H-150
 * studio 9-grid) can carry the style into the dispatch prompt — style_registry
 * ids live in MARKETING_DB, not the cards table, so card_ids alone would be
 * skipped server-side as unknown (verified in create-runtime routes/generate.ts).
 * @param {Array<{id: string, label?: string, status?: string, lora_id?: string, lora_strength?: number, prompt_fragment?: string, restyle_prompt?: string}>} styles
 * @returns {Array<{id: string, label: string, status: string, imageUrl: string, color: string, lora_id: string, lora_strength: number|null, prompt_fragment: string, restyle_prompt: string}>}
 */
export function mergeSeedImages(styles) {
  return (Array.isArray(styles) ? styles : [])
    .filter((s) => s && s.id != null)
    .map((s) => {
      const label = s.label || humanizeStyleId(s.id)
      const displayImg = seedImageUrl(s.id, label, s.sample_image_url)
      return {
        id: String(s.id),
        label,
        status: s.status || 'resident',
        imageUrl: displayImg,
        color: seedColor(s.id),
        lora_id: s.lora_id || 'none',
        lora_strength: s.lora_strength ?? null,
        prompt_fragment: s.prompt_fragment || '',
        restyle_prompt: s.restyle_prompt || '',
        sample_image_url: displayImg,
      }
    })
}

/**
 * Build the Inspiration-wall item list: the merged API pool, topped up with
 * the known seed ids when the API is unreachable/under-seeded so the wall
 * always shows at least `min` clickable tiles.
 * @param {Array} styles - raw style_registry rows (may be empty)
 * @param {number} [min=MIN_WALL_ITEMS]
 * @returns {ReturnType<typeof mergeSeedImages>}
 */
export function wallItems(styles, min = MIN_WALL_ITEMS) {
  const items = mergeSeedImages(styles)
  if (items.length >= min) return items
  const have = new Set(items.map((i) => i.id))
  const topUp = Object.keys(STYLE_SEED_COLORS)
    .filter((id) => !have.has(id))
    .slice(0, Math.max(0, min - items.length))
  return items.concat(mergeSeedImages(topUp.map((id) => ({ id, status: 'seed' }))))
}

/**
 * Resident style cards for the Community tab (card 2b): status='resident'
 * rows only. When the API yields none (offline / unseeded), fall back to the
 * known resident seed ids so the tab is never an empty promise.
 * @param {Array} styles - raw style_registry rows (may be empty)
 * @returns {ReturnType<typeof mergeSeedImages>}
 */
export function residentItems(styles) {
  const resident = mergeSeedImages(styles).filter((s) => s.status === 'resident')
  if (resident.length) return resident
  return mergeSeedImages(KNOWN_RESIDENT_IDS.map((id) => ({ id, status: 'resident' })))
}
